/* @flow */
import { action, observable } from 'mobx';

import { ApiModel } from 'api/ApiModel';
import { errorHandlerService, widgetStore } from 'services/ServiceRegistry';
import type { RoleId } from 'permissions/defs';
import { RoleMap } from 'permissions/defs';
import NavigationService from '../services/NavigationService';

const checkTypeOfUndefined = (value: any) => (typeof value === 'undefined' ? [] : value);

export type WidgetItem = {
  widget: string,
  position: number,
  variant: string,
};

export type TimePeriod = 'week' | 'month' | 'year';

type DashboardCountType = $Values<typeof DashboardCountsTypeMap>;
const DashboardCountsTypeMap = Object.freeze({
  ACCOUNT: 'accounts_count',
  COST_CENTRE: 'er_account_cost_centres_count',
  PROJECTS: 'er_account_projects_count',
  ASSETS_SOLD: 'er_assets_on_sold_count',
  ASSETS_OWNED: 'er_assets_owned_count',
  ASSETS_RENTED: 'er_rented_assets_count',
  OPEN_CONTRACTS: 'er_contracts_open_count',
  CLOSED_CONTRACTS: 'er_contracts_closed_count',
  LOCATIONS: 'er_locations_count',
  USER: 'users_count',
});

const url = NavigationService.formulateReportViewUri;
const DashboardCountsConfigMap = Object.freeze({
  ACCOUNT: {
    icon: '#icon-trading-accounts',
    label: 'general:trading_account_plural',
    link: '/accounts',
  },
  COST_CENTRE: {
    icon: '#icon-cost-centers',
    label: 'general:cost_center_plural',
    link: '/cost-centers',
  },
  PROJECTS: { icon: '#icon-projects', label: 'general:project_plural', link: '/projects' },
  ASSETS_SOLD: {
    icon: '#icon-assets',
    label: 'general:assets.sale_asset_plural',
    link: url(`/assets/`, {
      page: 1,
      filters: [{ key: 'asset_type', values: ['Purchased'] }],
    }),
  },
  ASSETS_OWNED: {
    icon: '#icon-assets',
    label: 'general:assets.owned_asset_plural',
    link: url(`/assets/`, {
      page: 1,
      filters: [{ key: 'asset_type', values: ['Owned'] }],
    }),
  },
  ASSETS_RENTED: {
    icon: '#icon-assets',
    label: 'general:assets.rental_asset_plural',
    link: url(`/assets/`, {
      page: 1,
      filters: [{ key: 'asset_status', values: ['On Rent'] }],
    }),
  },
  OPEN_CONTRACTS: {
    icon: '#icon-contracts',
    label: 'general:contract_open_plural',
    link: url(`/orders/`, {
      page: 1,
      filters: [{ key: 'er_status', values: ['open'] }],
    }),
  },
  CLOSED_CONTRACTS: {
    icon: '#icon-contracts',
    label: 'general:contract_closed_plural',
    link: url(`/orders/`, {
      page: 1,
      filters: [{ key: 'er_status', values: ['closed'], overrideImplicit: true }],
    }),
  },
  LOCATIONS: { icon: '#icon-locations', label: 'general:location_plural' },
  USER: { icon: '#icon-users', label: 'general:user_plural' },
});

export default class DashboardStore extends ApiModel {
  @observable loadingSentRequests: boolean = false;

  @observable sentRequests: Array<*> = [];

  @observable loadingAssignments: boolean = false;

  @observable assignments: Array<*> = [];

  @observable loadingLocations: boolean = false;

  @observable locations: Array<*> = [];

  @observable loadingAssetsByProject: boolean = false;

  @observable assetsByProjects: Array<*> = [];

  @observable assetsByModel: Array<*> = [];

  @observable loadingAssetsByUser: boolean = false;

  @observable assetsByUser: Array<*> = [];

  @observable users: Array<*> = [];

  @observable usersbyrole: Array<*> = [];

  @observable loadingUsers: boolean = false;

  @observable loadingUsersByRole: boolean = false;

  @observable qualitystates: Array<*> = [];

  @observable qualitystate: Array<*> = [];

  @observable loadingAssetQuality: boolean = false;

  @observable loadingAssetQualityType: boolean = false;

  @observable loadingContracts: boolean = false;

  @observable contracts: Object = {
    contracts: [],
    contractsCount: 0,
  };

  @observable loadingExpiredContracts: boolean = false;

  @observable expiredContracts: Object = {
    contracts: [],
    contractsCount: 0,
  };

  @observable loadingFinance: boolean = false;

  @observable finance: Array<*> = [];

  @observable loadingCostCenters: boolean = false;

  @observable costcenters: Array<*> = [];

  @observable loadingCalibrations: boolean = false;

  @observable calibrations: Array<*> = [];

  @observable loadingCalibrationsByTime = {
    week: false,
    month: false,
    year: false,
  };

  @observable calibrationsByTime = {
    week: {},
    month: {},
    year: {},
  };

  @observable loadingStatuses: boolean = false;

  @observable statuses: Array<*> = [];

  @observable loadingAssetsOverTime: boolean = false;

  @observable overtime: Array<*> = [];

  @observable loadingAssets: boolean = false;

  @observable assets: Array<*> = [];

  @observable loadingTradingAccounts: boolean = false;

  @observable accounts: Array<*> = [];

  @observable accountsbytime: Array<*> = [];

  @observable loadingProjects: boolean = false;

  @observable projects: Array<*> = [];

  @observable projectsbytime: Array<*> = [];

  @observable loadingAccounts: boolean = false;

  @observable myaccounts: Array<*> = [];

  @observable loadedTradingAccount: string = 0;

  @observable loadingAssetsByModel: boolean = false;

  @observable fullScreenWidget: string | null = null;

  @observable loadingCounts: Boolean = false;

  @observable counts: Object = {};

  widgetConfigLoaded = false;

  migrateOldWidgetConfig() {
    const _localStoreLayout = localStorage.getItem('dashboardLayout');
    const _localStoreFavourites = localStorage.getItem('favouriteWidgets');
    let widgetConfig: Object | WidgetItem[] = {};
    let favouriteWidgets: string[];
    let widgets: ?(WidgetItem[]);

    if (typeof _localStoreLayout === 'string') {
      try {
        widgetConfig = JSON.parse(_localStoreLayout);
      } catch (_err) {}

      widgets = [];
    }

    if (typeof _localStoreFavourites === 'string') {
      favouriteWidgets = [];
      this.favouriteWidgetsMigrating = true;
      try {
        favouriteWidgets = JSON.parse(_localStoreFavourites);
      } catch (_err) {}
    }

    // Pre-array config migration
    if (widgets) {
      if (widgetConfig.widget1 || widgetConfig.widget2) {
        if (widgetConfig.widget1) {
          widgets.push({
            position: 1,
            variant: 'default',
            // flow-disable-next-line Flow bug
            widget: widgetConfig.widget1,
          });
        }

        if (widgetConfig.widget2) {
          widgets.push({
            position: 2,
            variant: 'default',
            // flow-disable-next-line Flow bug
            widget: widgetConfig.widget2,
          });
        }
      } else {
        widgets.push(...widgetConfig);
      }
    }

    localStorage.removeItem('dashboardLayout');
    localStorage.removeItem('favouriteWidgets');

    return {
      widgets,
      favouriteWidgets,
    };
  }

  @action
  loadWidgetConfig() {
    let { widgets, favouriteWidgets } = this.migrateOldWidgetConfig();

    if (widgets) {
      widgetStore.widgets = widgets;
    }

    if (favouriteWidgets) {
      widgetStore.favouriteWidgets = favouriteWidgets;
    }

    this.widgetConfigLoaded = true;
    return { widgets, favouriteWidgets };
  }

  @action
  async loadSentRequests(account: string): Promise<void> {
    this.loadingSentRequests = true;

    let allSentRequests: Object;

    try {
      allSentRequests = await this.api.getResource(`dashboard?chart=sent-requests&id=${account}`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.sentRequests = checkTypeOfUndefined(allSentRequests);

    this.loadingSentRequests = false;

    return allSentRequests;
  }

  @action
  async loadAssignments(account: string): Promise<void> {
    this.loadingAssignments = true;

    let allAssignments: Object;

    try {
      allAssignments = await this.api.getResource(
        `dashboard?chart=assets-assignment&id=${account}`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.assignments = checkTypeOfUndefined(allAssignments);

    this.loadingAssignments = false;
  }

  @action
  async loadLocations(account: string): Promise<void> {
    this.loadingLocations = true;

    let allLocations: Object;

    try {
      allLocations = await this.api.getResource(`dashboard?chart=assets-location&id=${account}`);

      this.locations = checkTypeOfUndefined(allLocations)
        ['assets-location'].filter((item: Object) => {
          return item.location_address !== null;
        })
        .sort((a, b) => b.assets - a.assets);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.loadingLocations = false;
  }

  @action
  async loadAssetsByProject(account: string): Promise<void> {
    this.loadingAssetsByProject = true;

    let allAssetsByProject: Object;

    try {
      if (account === 0) {
        allAssetsByProject = await this.api.getResource('dashboard?chart=assets-project&limit=16');
      } else {
        allAssetsByProject = await this.api.getResource(
          `dashboard?chart=assets-project&id=${account}&limit=16`
        );
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.assetsByProjects = checkTypeOfUndefined(allAssetsByProject);

    this.loadingAssetsByProject = false;
  }

  @action
  async loadAssetsByModel(account: string): Promise<void> {
    this.loadingAssetsByModel = true;

    let allAssetsByModel: Object;

    try {
      if (account === 0) {
        allAssetsByModel = await this.api.getResource('dashboard?chart=assets-products&limit=7');
      } else {
        allAssetsByModel = await this.api.getResource(
          `dashboard?chart=assets-products&id=${account}&limit=7`
        );
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.assetsByModel = checkTypeOfUndefined(allAssetsByModel);

    this.loadingAssetsByModel = false;
  }

  @action
  async loadAssetsByUser(account: string, limit = null): Promise<void> {
    this.loadingAssetsByUser = true;

    let allAssetsByUser: Object;

    try {
      let url = `dashboard?chart=assets-user&id=${account}&limit=${limit}`;

      allAssetsByUser = await this.api.getResource(url);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.assetsByUser = checkTypeOfUndefined(allAssetsByUser);

    this.loadingAssetsByUser = false;
  }

  @action
  async loadAssetsByQuality(account: string): Promise<void> {
    this.loadingAssetQuality = true;

    let allQualityStates: Object;

    try {
      allQualityStates = await this.api.getResource(`dashboard?chart=assets-quality&id=${account}`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.loadingAssetQuality = false;

    this.qualitystates = checkTypeOfUndefined(allQualityStates);
  }

  @action
  async loadAssetsByQualityType(account: string, type: string): Promise<void> {
    this.loadingAssetQualityType = true;

    let qualityState: Object;

    try {
      qualityState = await this.api.getResource(
        `dashboard?chart=assets-quality&id=${account}&status=${type}`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.loadingAssetQualityType = false;

    this.qualitystate = checkTypeOfUndefined(qualityState);
  }

  @action
  async loadUsers(account: string): Promise<void> {
    this.loadingUsers = true;

    let allUsers: Object;

    try {
      if (account === 0) {
        allUsers = await this.api.getResource('dashboard?chart=users');
      } else {
        allUsers = await this.api.getResource(`dashboard?chart=users&id=${account}`);
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.users = checkTypeOfUndefined(allUsers);

    this.loadingUsers = false;
  }

  @action
  async loadUsersByRole(account: string, type: RoleId | string): Promise<void> {
    this.loadingUsersByRole = true;
    const _type = isNaN(Number(type)) ? type : RoleMap[Number(type)];

    let usersByRole: Object;

    try {
      if (account === 0) {
        usersByRole = await this.api.getResource(`dashboard?chart=users&role=${_type}`);
      } else {
        usersByRole = await this.api.getResource(
          `dashboard?chart=users&id=${account}&role=${_type}`
        );
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.usersbyrole = checkTypeOfUndefined(usersByRole);

    this.loadingUsersByRole = false;
  }

  @action
  async loadContracts(account: string): Promise<void> {
    this.loadingContracts = true;

    let allContracts: Object;

    try {
      allContracts = await this.api.getResource(`dashboard?chart=contracts&id=${account}&limit=5`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.contracts = checkTypeOfUndefined(allContracts);

    this.loadingContracts = false;
  }

  @action
  async loadExpiredContracts(account: string): Promise<void> {
    this.loadingExpiredContracts = true;

    let allContracts: Object;

    try {
      allContracts = await this.api.getResource(
        `dashboard?chart=contracts&id=${account}&limit=5&expired=true`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.expiredContracts = checkTypeOfUndefined(allContracts);
    this.loadingExpiredContracts = false;
  }

  @action
  async loadProjectFinance(account: string): Promise<void> {
    this.loadingFinance = true;

    let allFinance: Object;

    try {
      allFinance = await this.api.getResource(`dashboard?chart=project-finance&id=${account}`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.finance = checkTypeOfUndefined(allFinance);

    this.loadingFinance = false;
  }

  @action
  async loadCostCenters(account: string): Promise<void> {
    this.loadingCostCenters = true;

    let allCostCenters: Object;

    try {
      if (account === 0) {
        allCostCenters = await this.api.getResource('dashboard?chart=cost-centres');
      } else {
        allCostCenters = await this.api.getResource(`dashboard?chart=cost-centres&id=${account}`);
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.costcenters = checkTypeOfUndefined(allCostCenters);

    this.loadingCostCenters = false;
  }

  @action
  async loadCalibrations(account: string): Promise<void> {
    this.loadingCalibrations = true;

    let allCalibrations: Object;

    try {
      allCalibrations = await this.api.getResource(
        `dashboard?chart=upcoming-calibration&id=${account}`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.calibrations = checkTypeOfUndefined(allCalibrations);

    this.loadingCalibrations = false;
  }

  @action
  async loadCalibrationsByTime(account: string, time: TimePeriod): Promise<void> {
    this.loadingCalibrationsByTime[time] = true;

    let calibrationByTime: Object;

    try {
      calibrationByTime = await this.api.getResource(
        `dashboard?chart=upcoming-calibration&id=${account}&status=${time}`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.calibrationsByTime[time] = checkTypeOfUndefined(calibrationByTime);

    this.loadingCalibrationsByTime[time] = false;
  }

  @action
  async loadAssetStatus(account: string): Promise<void> {
    this.loadingStatuses = true;

    let allStatuses: Object;

    try {
      allStatuses = await this.api.getResource(`dashboard?chart=assets-status&id=${account}`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.statuses = checkTypeOfUndefined(allStatuses);

    this.loadingStatuses = false;
  }

  @action
  async loadAssets(account: string): Promise<void> {
    this.loadingAssets = true;

    let allAssets: Object;

    try {
      allAssets = await this.api.getResource(`dashboard?chart=assets&id=${account}`);
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.assets = checkTypeOfUndefined(allAssets);

    this.loadingAssets = false;
  }

  @action
  async loadAssetsOverTime(account: string): Promise<void> {
    this.loadingAssetsOverTime = true;

    let allAssetsOverTime: Object;

    try {
      allAssetsOverTime = await this.api.getResource(
        `dashboard?chart=assets-over-time&id=${account}`
      );
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.overtime = checkTypeOfUndefined(allAssetsOverTime);

    this.loadingAssetsOverTime = false;
  }

  @action
  async loadTradingAccounts(account: string, time?: string): Promise<void> {
    this.loadingTradingAccounts = true;

    let allTradingAccounts: Object;
    let tradingAccountsByTime: Object | null;

    try {
      if (account === 0) {
        allTradingAccounts = await this.api.getResource('dashboard?chart=trading-accounts');
        tradingAccountsByTime = time
          ? await this.api.getResource(`dashboard?chart=trading-accounts&status=${time}`)
          : null;
      } else {
        allTradingAccounts = await this.api.getResource(
          `dashboard?chart=trading-accounts&id=${account}`
        );
        tradingAccountsByTime = time
          ? await this.api.getResource(
              `dashboard?chart=trading-accounts&id=${account}&status=${time}`
            )
          : null;
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.accounts = checkTypeOfUndefined(allTradingAccounts);
    this.accountsbytime = checkTypeOfUndefined(tradingAccountsByTime);

    this.loadingTradingAccounts = false;
  }

  @action
  async loadProjects(account: string, time?: string): Promise<void> {
    this.loadingProjects = true;

    let allProjects: Object;
    let projectsByTime: Object | null;

    try {
      if (account === 0) {
        allProjects = await this.api.getResource('dashboard?chart=projects');
        projectsByTime = time
          ? await this.api.getResource(`dashboard?chart=projects&status=${time}`)
          : null;
      } else {
        allProjects = await this.api.getResource(`dashboard?chart=projects&id=${account}`);
        projectsByTime = time
          ? await this.api.getResource(`dashboard?chart=projects&id=${account}&status=${time}`)
          : null;
      }
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.projects = checkTypeOfUndefined(allProjects);
    this.projectsbytime = checkTypeOfUndefined(projectsByTime);

    this.loadingProjects = false;
  }

  @action
  async loadAccounts(): Promise<void> {
    this.loadingAccounts = true;

    let myAccounts: Object;

    try {
      myAccounts = await this.api.getResource('my-accounts?only_enabled=true&page_size=1000');
    } catch (err) {
      errorHandlerService.handleException(err);
    }

    this.myaccounts = checkTypeOfUndefined(myAccounts);

    this.loadingAccounts = false;
  }

  @action
  async loadDashboardCounts(): Promise<void> {
    this.loadingCounts = true;

    this.counts = await this.api.getResource('dashboard-counts');

    this.loadingCounts = false;

    return this.counts;
  }

  getDashboardCountConfig(dashboardCount: DashboardCountType) {
    const type = Object.keys(DashboardCountsTypeMap).find(
      key => DashboardCountsTypeMap[key] === dashboardCount
    );

    return DashboardCountsConfigMap[type];
  }

  @action
  storeTradingAccount(id: string): Promise<void> {
    this.loadedTradingAccount = id;
  }

  removeDashboardFromLocal() {
    if (typeof Storage !== 'undefined') {
      localStorage.removeItem('dashboardLayout');
      localStorage.removeItem('dashboardTradingAccount');
      localStorage.removeItem('favouriteWidgets');
    }
  }
}
